import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition';
import { FilterSelections } from '@lambda-layer/types/filter';
import { CountMapUtils } from '@/utils/CountMapUtils';
import log from 'loglevel';

import { ListRepository } from '@/repository/list/ListRepository';
import { GenericService } from '@/service/GenericService.ts';
import { ExclusionRule } from '@/API';

log.setLevel(log.levels.INFO);

export abstract class ListService<T> extends GenericService<T> {
  protected repository: ListRepository<T>;

  constructor(repository: ListRepository<T>) {
    super(repository);
    this.repository = repository;
  }

  async getFilterCountMap(
    fieldId: string,
    filterDefinitions: FilterDefinition[],
    filterSelections: FilterSelections,
    exclusions: ExclusionRule[] = []
  ): Promise<Map<string | null, number>> {
    log.debug(
      `Fetching counts for ${fieldId} filter and selections:`,
      filterSelections
    );

    const fieldIndex = filterDefinitions.findIndex(f => f.id === fieldId);
    const field = filterDefinitions[fieldIndex];

    // Skip count fetching for non-database fields
    if (!field || field.csvMetadata?.fieldType !== 'database') {
      log.warn(`Skipping count fetching for non-database field: ${fieldId}`);
      return new Map<string, number>();
    }

    const values = filterSelections ? filterSelections[fieldId]?.value : [];
    log.debug(`Checking selections for ${fieldId}:`, values);

    // Always use aggregate for aggregate or array fields to get bucket and total
    const useAggregate = field.isAggregateField || field.isArrayField;

    // Fetch aggregate buckets and total for aggregate or array fields; otherwise count
    let countData: Map<string | null, number>;
    if (useAggregate) {
      if (filterSelections) {
        const dbColumn = field.databaseMetadata?.columnName;
        if (!dbColumn) {
          throw new Error(
            `Could not find database column for filter field ${fieldId}`
          );
        }

        countData = await this.repository.aggregate(
          dbColumn,
          filterDefinitions,
          filterSelections,
          exclusions
        );
      } else {
        countData = new Map<string | null, number>();
      }
    } else {
      if (filterSelections) {
        const totalCount = await this.repository.count(
          filterDefinitions,
          filterSelections,
          exclusions
        );
        countData = CountMapUtils.createTotalCountMap(totalCount);
      } else {
        countData = new Map<string | null, number>();
      }
    }

    const booleanConvertedMap = Array.from(countData.entries()).map(
      ([id, count]) => {
        if (field.databaseMetadata?.dataType === 'boolean') {
          // Preserve the special 'count' key without conversion
          if (CountMapUtils.isTotalCountKey(id)) {
            return [id, count];
          }
          // Handle null values explicitly
          if (id === null || id === undefined) {
            return [null, count];
          }
          return [id === '1' ? 'true' : 'false', count];
        }
        return [id, count];
      }
    );

    return new Map(
      booleanConvertedMap as Iterable<readonly [string | null, number]>
    );
  }
}
